import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Divider,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import {
  formatHours,
  formatPercent,
  formatTime,
  GenerateResponse,
  SeverityBadge,
  shortWeekday,
  statusMeta,
  text,
  Verification,
  Violation,
  WEEKDAY_ORDER,
  weekdayName,
  WithinLimitsBadge,
} from "../ai-weekly-template-builder.utils";

/**
 * Deliberately narrower than `GenerateResponse`: a stored suggestion from the
 * history has no surrounding context, and none is needed. The only thing that
 * ever came from it was the rate-limit line, which cannot apply to a result that
 * was actually produced.
 */
interface Props {
  status: GenerateResponse["status"];
  suggestion: GenerateResponse["suggestion"];
  verification: GenerateResponse["verification"];
  /**
   * Only set for a live generation that was rate limited. Derived from the
   * response rather than written out, because the API layer revives it into a
   * js-joda Instant rather than the string the type would suggest.
   */
  nextAvailableAt?: GenerateResponse["context"]["rateLimit"]["nextAvailableAt"];
  /**
   * The "create this template" action, injected rather than built here.
   *
   * Passed in as a node so this component stays presentational and keeps taking
   * only the three pieces it renders — the modal owns the apply state, and both
   * of its call sites get the action from one place.
   */
  applyBar?: ReactNode;
  /**
   * Navigation between the versions of this suggestion, injected on the same
   * terms as `applyBar`. Rendered above the verdict: it says which result you are
   * looking at, so it has to come before the verdict on that result.
   */
  versionStrip?: ReactNode;
  /** "Change this" for the result on screen. Injected likewise. */
  feedbackBox?: ReactNode;
}

/**
 * Renders `verification` — the scheduling system's own recomputation — and never
 * `suggestion`'s numbers. The assistant's prose is shown alongside, but every
 * figure on screen is one the server derived itself.
 *
 * Used for both a live generation and a stored one from the history, which is
 * why it takes the three pieces rather than the whole response.
 */
export default function AiWeeklyTemplateResultPanel(props: Props) {
  const { status, suggestion, verification } = props;
  const meta = statusMeta(status);

  const errors = verification?.violations.filter((v) => v.severity === "ERROR") ?? [];
  const nonErrors = verification?.violations.filter((v) => v.severity !== "ERROR") ?? [];

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {props.versionStrip}

      <Alert status={meta.alert}>
        <AlertIcon />
        <Box>
          <AlertTitle>{meta.title}</AlertTitle>
          <AlertDescription fontSize="sm">{meta.description}</AlertDescription>
        </Box>
      </Alert>

      {status === "RATE_LIMITED" &&
        props.nextAvailableAt !== undefined &&
        props.nextAvailableAt !== null && (
          <Text fontSize="sm">Next generation available at {text(props.nextAvailableAt)}.</Text>
        )}

      {/*
        Directly under the verdict rather than at the bottom of the panel. A real
        result runs to several tables inside a scrolling ModalBody, so anything
        below them is off screen every time — and "already applied" is status
        information, which belongs next to the other status information.
      */}
      {props.applyBar}

      {/* Same band as the apply bar, and for the same reason: it is one of the
          two things to do with this result. */}
      {props.feedbackBox}

      {verification === null ? null : (
        <>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <Stat>
              <StatLabel>Weekly total</StatLabel>
              <StatNumber fontSize="lg">{formatHours(verification.weeklyTotalMinutes)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total scheduled</StatLabel>
              <StatNumber fontSize="lg">
                {formatHours(verification.totalScheduledMinutes)}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total authorized</StatLabel>
              <StatNumber fontSize="lg">
                {formatHours(verification.totalAuthorizedMinutes)}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Utilization</StatLabel>
              <StatNumber fontSize="lg">
                {formatPercent(verification.realisticUtilizationPercent)}
              </StatNumber>
              <Text color="gray.500" fontSize="xs">
                {formatPercent(verification.utilizationPercent)} of the un-prorated total
              </Text>
            </Stat>
          </SimpleGrid>

          <Divider />

          <Box>
            <Heading pb={2} size="sm">
              Recommended weekly schedule
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 7 }} spacing={2}>
              {WEEKDAY_ORDER.map((weekday) => {
                // Compare by name, not identity: `dayOfWeek` is a js-joda object
                // at runtime, so `=== "MONDAY"` would never match and the grid
                // would silently render empty.
                const shifts = verification.weeklyShifts.filter(
                  (shift) => weekdayName(shift.dayOfWeek) === weekday
                );
                return (
                  <Box
                    key={weekday}
                    bg={shifts.length === 0 ? "gray.50" : "blue.50"}
                    borderRadius="md"
                    minH="70px"
                    p={2}
                  >
                    <Text fontSize="xs" fontWeight="bold" textTransform="uppercase">
                      {shortWeekday(weekday)}
                    </Text>
                    {shifts.length === 0 ? (
                      <Text color="gray.400" fontSize="xs">
                        —
                      </Text>
                    ) : (
                      shifts.map((shift, index) => (
                        <Box key={index}>
                          <Text fontSize="sm" fontWeight="semibold">
                            {formatTime(shift.startTime)}–{formatTime(shift.endTime)}
                          </Text>
                          <Text color="gray.600" fontSize="xs">
                            {formatHours(shift.durationMinutes)}
                            {shift.crossesMidnight ? " (overnight)" : ""}
                          </Text>
                        </Box>
                      ))
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>

          {errors.length > 0 && (
            <ViolationList
              description="Do not use this schedule until these are resolved."
              status="error"
              title="Errors"
              violations={errors}
            />
          )}

          {nonErrors.length > 0 && (
            <ViolationList
              description="Worth checking before you enter this schedule."
              status="warning"
              title="Notes"
              violations={nonErrors}
            />
          )}

          {verification.discrepancies.length > 0 && (
            <Box overflowX="auto">
              <Heading pb={1} size="sm">
                Where the assistant&apos;s arithmetic disagreed
              </Heading>
              <Text color="gray.600" fontSize="sm" pb={2}>
                Shown for transparency. Every figure elsewhere on this screen is the verified one.
              </Text>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Field</Th>
                    <Th isNumeric>Assistant said</Th>
                    <Th isNumeric>Verified</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {verification.discrepancies.map((discrepancy, index) => (
                    <Tr key={index}>
                      <Td fontSize="xs">{discrepancy.field}</Td>
                      <Td isNumeric>{discrepancy.assistantValue}</Td>
                      <Td isNumeric>{discrepancy.serverValue}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}

          <Box overflowX="auto">
            <Heading pb={1} size="sm">
              Validation by authorization period
            </Heading>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Period</Th>
                  <Th isNumeric>Limit</Th>
                  <Th isNumeric>Recurring</Th>
                  <Th isNumeric>True-up</Th>
                  <Th isNumeric>Final</Th>
                  <Th isNumeric>Unused</Th>
                  <Th isNumeric>Utilization</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {verification.periodRows.map((row) => (
                  <Tr key={row.periodIndex}>
                    <Td>
                      {row.label}
                      {row.isPartialPeriod && (
                        <Text as="span" color="orange.500" fontSize="xs">
                          {" "}
                          (partial)
                        </Text>
                      )}
                    </Td>
                    <Td isNumeric>{formatHours(row.periodLimitMinutes)}</Td>
                    <Td isNumeric>{formatHours(row.recurringMinutes)}</Td>
                    <Td isNumeric>{formatHours(row.trueUpMinutes)}</Td>
                    <Td isNumeric>{formatHours(row.finalScheduledMinutes)}</Td>
                    <Td isNumeric>{formatHours(row.unusedMinutes)}</Td>
                    <Td isNumeric>
                      {formatPercent(
                        row.isPartialPeriod
                          ? row.realisticUtilizationPercent
                          : row.utilizationPercent
                      )}
                    </Td>
                    <Td>
                      <WithinLimitsBadge withinLimits={row.withinLimits} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {verification.trueUpRows.length > 0 && (
            <Box overflowX="auto">
              <Heading pb={1} size="sm">
                True-up schedule
              </Heading>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Day</Th>
                    <Th isNumeric>Additional</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {verification.trueUpRows.map((row, index) => (
                    <Tr key={index}>
                      <Td>{text(row.date)}</Td>
                      <Td>{shortWeekday(row.dayOfWeek)}</Td>
                      <Td isNumeric>{formatHours(row.additionalMinutes)}</Td>
                      <Td fontSize="xs">{trueUpStatus(row)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </>
      )}

      {suggestion !== null && (
        <Box>
          <Heading pb={1} size="sm">
            Why this schedule
          </Heading>
          <Text fontSize="sm">{suggestion.explanation}</Text>

          {suggestion.multipleSchedulesRequiredReason !== null && (
            <Alert mt={2} status="warning">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {suggestion.multipleSchedulesRequiredReason}
              </AlertDescription>
            </Alert>
          )}

          <NarrativeList
            items={suggestion.ruleInterpretations.map(
              (interpretation) => `${interpretation.rule} → ${interpretation.appliedAs}`
            )}
            title="How your rules were applied"
          />
          <NarrativeList
            items={suggestion.unusedMinutesExplanations}
            title="Why some hours are unused"
          />
          <NarrativeList items={suggestion.assumptions} title="Assumptions" />
        </Box>
      )}
    </Box>
  );
}

function ViolationList(props: {
  description: string;
  status: "error" | "warning";
  title: string;
  violations: Violation[];
}) {
  return (
    <Alert alignItems="flex-start" status={props.status}>
      <AlertIcon />
      <Box>
        <AlertTitle>{props.title}</AlertTitle>
        <AlertDescription fontSize="sm">{props.description}</AlertDescription>
        <UnorderedList pt={2}>
          {props.violations.map((violation, index) => (
            <ListItem key={index} fontSize="sm">
              <SeverityBadge severity={violation.severity} /> {violation.message}
            </ListItem>
          ))}
        </UnorderedList>
      </Box>
    </Alert>
  );
}

/**
 * A true-up normally lands on the period's last service day, but a leftover that
 * will not fit there is carried backwards through earlier service days. Saying
 * how far back it went explains why a top-up is on a date the coordinator might
 * not expect.
 */
function trueUpStatus(row: Verification["trueUpRows"][number]): string {
  if (!row.accepted) {
    return `Rejected — ${row.rejectionReason ?? "see errors"}`;
  }
  if (row.isLastServiceDayOfPeriod) {
    return "Accepted — last service day of the period";
  }
  if (row.serviceDaysFromEndOfPeriod === null) {
    return "Accepted — but further back than the allowed lookback";
  }
  const days = row.serviceDaysFromEndOfPeriod;
  return `Accepted — ${days} service day${days === 1 ? "" : "s"} before the end of the period`;
}

function NarrativeList(props: { items: string[]; title: string }) {
  if (props.items.length === 0) {
    return null;
  }
  return (
    <Box pt={3}>
      <Heading pb={1} size="xs">
        {props.title}
      </Heading>
      <UnorderedList>
        {props.items.map((item, index) => (
          <ListItem key={index} fontSize="sm">
            {item}
          </ListItem>
        ))}
      </UnorderedList>
    </Box>
  );
}
